import { useQueryClient } from "@tanstack/react-query";
import { GraphQLClient } from "graphql-request";
import { useRef } from "react";

import { Day, PendingPunch, Punch } from "../../generated-types";
import { useCreatePendingPunchMutation } from "../pending-punches/index.generated";
import {
  GetDaysByUserQuery,
  GetDaysByUserQueryVariables,
  useGetDaysByUserQuery
} from "../../queries/days/index.generated";
import {
  useCreatePunchMutation,
  useDeletePunchMutation,
  useUpdatePunchMutation
} from "./index.generated";

export const useCreatePunch = ({
  day,
  setCurrentDay,
  client,
  variables
}: {
  day?: Day | null;
  setCurrentDay?: React.Dispatch<React.SetStateAction<Day | undefined | null>>;
  client: GraphQLClient;
  variables: GetDaysByUserQueryVariables;
}) => {
  const queryClient = useQueryClient();

  const { mutate: createPunchMutation, ...options } = useCreatePunchMutation(
    client,
    {
      onSuccess: (data) => {
        const daysData: GetDaysByUserQuery | null | undefined =
          queryClient.getQueryData(useGetDaysByUserQuery.getKey(variables));

        console.log("hook daysData ", daysData);

        const newPunch = data?.createPunch as Punch;

        const newDay: Day = {
          ...(day as Day),
          punches: [...((day as Day).punches ?? []), newPunch]
        };

        // se esiste
        if (setCurrentDay) {
          setCurrentDay(newDay);
        }

        const newData = daysData?.days?.map((_day) => {
          if (day?.documentId === _day?.documentId) {
            return newDay;
          }
          return _day;
        });

        const _data = {
          days: newData
        };

        if (daysData) {
          queryClient.setQueryData(
            useGetDaysByUserQuery.getKey(variables),
            (_old: any) => _data
          );
        }
      }
    }
  );

  return { createPunchMutation, options };
};

export const useDeletePunch = ({
  day,
  setCurrentDay,
  client,
  variables
}: {
  day?: Day;
  setCurrentDay?: React.Dispatch<React.SetStateAction<Day | undefined | null>>;
  client: GraphQLClient;
  variables: GetDaysByUserQueryVariables;
}) => {
  const queryClient = useQueryClient();

  const { mutate: deletePunchMutation, ...options } = useDeletePunchMutation(
    client,
    {
      onSuccess: (data) => {
        const daysData: GetDaysByUserQuery | null | undefined =
          queryClient.getQueryData(useGetDaysByUserQuery.getKey(variables));

        // eliminare dalla cache
        const newPunches =
          day?.punches?.filter(
            (punch) => punch?.documentId !== data?.deletePunch?.documentId
          ) ?? [];

        const newDay: Day = {
          ...(day as Day),
          punches: newPunches
        };
        // se esiste
        if (setCurrentDay) {
          setCurrentDay(newDay);
        }

        const newData = daysData?.days?.map((_day) => {
          if (day?.documentId === _day?.documentId) {
            return newDay;
          }
          return _day;
        });

        const _data = {
          days: newData
        };

        if (daysData) {
          queryClient.setQueryData(
            useGetDaysByUserQuery.getKey(variables),
            (_old) => _data
          );
        }
      }
    }
  );

  return { deletePunchMutation, options };
};

export const useUpdatePunch = ({
  day,
  setCurrentDay,
  client,
  variables
}: {
  day?: Day;
  setCurrentDay?: React.Dispatch<React.SetStateAction<Day | undefined | null>>;
  client: GraphQLClient;
  variables: GetDaysByUserQueryVariables;
}) => {
  const queryClient = useQueryClient();

  const { mutate: updatePunchMutation, ...options } = useUpdatePunchMutation(
    client,
    {
      onSuccess: (data) => {
        const daysData: GetDaysByUserQuery | null | undefined =
          queryClient.getQueryData(useGetDaysByUserQuery.getKey(variables));

        console.log("hook daysData", daysData);

        //  aggiorno la cache
        const newPunches =
          day?.punches?.map((punch) => {
            if (punch?.documentId === data?.updatePunch?.documentId) {
              const updatedPunch = data?.updatePunch as Punch;
              // Preserva le pendingPunches esistenti se non vengono ritornate dalla mutation
              const pendingPunches = updatedPunch.pendingPunches?.length
                ? updatedPunch.pendingPunches
                : punch?.pendingPunches || [];

              return {
                ...updatedPunch,
                pendingPunches
              } as Punch;
            }
            return punch as Punch;
          }) ?? [];

        const newDay: Day = {
          ...(day as Day),
          punches: newPunches
        };

        // se esiste
        if (setCurrentDay) {
          console.log("newDay", newDay);
          setCurrentDay(newDay);
        }

        const newData = daysData?.days?.map((_day) => {
          if (day?.documentId === _day?.documentId) {
            return newDay;
          }
          return _day;
        });

        const _data = {
          days: newData
        };

        if (daysData) {
          queryClient.setQueryData(
            useGetDaysByUserQuery.getKey(variables),
            (_old) => _data
          );
        }
      }
    }
  );

  return { updatePunchMutation, options };
};

export const useCreatePastPunch = ({
  day,
  setCurrentDay,
  client,
  variables
}: {
  day?: Day | null;
  setCurrentDay?: React.Dispatch<React.SetStateAction<Day | undefined | null>>;
  client: GraphQLClient;
  variables: GetDaysByUserQueryVariables;
}) => {
  const queryClient = useQueryClient();
  const pendingTimesRef = useRef<{ in?: string | null; out?: string | null } | null>(null);

  const { mutate: createPendingPunch, isLoading: pendingLoading } = useCreatePendingPunchMutation(client, {
    onSuccess: (pendingData, mutationVariables) => {
      const newPendingPunch = pendingData?.createPendingPunch as PendingPunch;
      const punchId = mutationVariables.punchId as string;

      const daysData = queryClient.getQueryData<GetDaysByUserQuery>(
        useGetDaysByUserQuery.getKey(variables)
      );
      const currentDayInCache = daysData?.days?.find(
        (d) => d?.documentId === day?.documentId
      ) as Day | undefined;

      const updatedPunches = (currentDayInCache?.punches || []).map((punch) => {
        if (punch?.documentId === punchId) {
          return {
            ...punch,
            pendingPunches: [...(punch?.pendingPunches || []), newPendingPunch]
          } as Punch;
        }
        return punch;
      });

      const newDay: Day = {
        ...(currentDayInCache as Day),
        punches: updatedPunches,
        pendings: [...(currentDayInCache?.pendings || []), newPendingPunch]
      };

      if (setCurrentDay) setCurrentDay(newDay);

      if (daysData) {
        queryClient.setQueryData(
          useGetDaysByUserQuery.getKey(variables),
          (_old: any) => ({
            days: daysData.days?.map((_day) =>
              day?.documentId === _day?.documentId ? newDay : _day
            )
          })
        );
      }
    }
  });

  const { mutate: createPunchMutation, isLoading: punchLoading, ...options } =
    useCreatePunchMutation(client, {
      onSuccess: (data) => {
        const newPunch = data?.createPunch as Punch;

        const daysData = queryClient.getQueryData<GetDaysByUserQuery>(
          useGetDaysByUserQuery.getKey(variables)
        );

        const newDay: Day = {
          ...(day as Day),
          punches: [...((day as Day).punches ?? []), newPunch]
        };

        if (setCurrentDay) setCurrentDay(newDay);

        if (daysData) {
          queryClient.setQueryData(
            useGetDaysByUserQuery.getKey(variables),
            (_old: any) => ({
              days: daysData.days?.map((_day) =>
                day?.documentId === _day?.documentId ? newDay : _day
              )
            })
          );
        }

        if (pendingTimesRef.current && newPunch?.documentId) {
          createPendingPunch({
            dayId: day?.documentId as string,
            punchId: newPunch.documentId,
            in: pendingTimesRef.current.in ?? undefined,
            out: pendingTimesRef.current.out ?? undefined,
            penalty: 10
          });
        }
      }
    });

  const createPastPunchMutation = (params: {
    dayId: string;
    location: string;
    in?: string | null;
    out?: string | null;
  }) => {
    pendingTimesRef.current = { in: params.in, out: params.out };
    createPunchMutation({ dayId: params.dayId, location: params.location });
  };

  return {
    createPastPunchMutation,
    options: { ...options, isLoading: punchLoading || pendingLoading }
  };
};
